import type { ShouldRevalidateFunctionArgs } from 'react-router';

import { useAppConfig } from '#/config/AppConfigContext';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { CreateListingApplicationFromTmpStorageContainer } from '#/modules/listing/containers/CreateListingApplicationFromTmpStorageContainer';
import { isListingExpired } from '#/modules/listing/helpers/isListingExpired';
import { listingApiIsomorphicFetchInternship } from '#/modules/listing/internships/api/listingApiFetchInternship';
import { ExpiredInternshipPageLoadable } from '#/modules/listing/internships/pages/expired/ExpiredInternshipPageLoadable';
import { UnpublishedInternshipPageLoadable } from '#/modules/listing/internships/pages/unpublished/UnpublishedInternshipPageLoadable';
import { viewInternshipReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewInternshipPageLoadable } from './ViewInternshipPageLoadable';

export const handle = {
  defaultSearchType: 'INTERNSHIP',
  showDesktopSearch: true,
  showSearchRefinements: true,
  hideFooterDivider: true,
} satisfies LayoutHandleProps;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  const removedListingApplicationTmpId =
    currentUrl.searchParams.has('listingApplicationTmpId') &&
    !nextUrl.searchParams.has('listingApplicationTmpId');

  if (removedListingApplicationTmpId) return false;

  return defaultShouldRevalidate;
}

export async function loader({
  request,
  context,
  params: { internshipIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewInternshipReferenceRoute>) {
  const [internshipId] = internshipIdDashSlug.split('-');

  try {
    const [internship] = await Promise.all([
      listingApiIsomorphicFetchInternship({ request, context })(internshipId),
      // It's possible this loadable won't be used if the internship is unpublished
      // but in most cases optimistically loading it is best
      ViewInternshipPageLoadable.load(),
    ]);

    const fetchedListingExtras =
      await userDashboardApiIsomorphicFetchListingExtras({
        request,
        context,
      })([internshipId, internship.org?.id].filter(Boolean));

    const { saved, applicationId } = fetchedListingExtras[internshipId] ?? {
      saved: false,
      applicationId: null,
    };

    const orgSaved = Boolean(
      internship.org && fetchedListingExtras[internship.org.id]?.saved,
    );

    switch (internship.type) {
      case 'INTERNSHIP':
        return {
          type: isListingExpired(internship.expires)
            ? 'EXPIRED_INTERNSHIP'
            : internship.type,
          internship,
          locale: context.locale,
          handle: {
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: internship.url,
          } satisfies LayoutHandleProps,
          applicationId,
          saved,
          orgSaved,
        } as const;
      case 'UNPUBLISHED_INTERNSHIP':
        await UnpublishedInternshipPageLoadable.load();
        return idealistNotFound({
          type: internship.type,
          unpublishedInternship: internship,
        } as const);
    }
  } catch {
    return idealistNotFound(null);
  }
}

export default function ViewInternshipHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  switch (loaderData?.type) {
    case undefined:
      return <NotFoundPageLoadable />;
    case 'INTERNSHIP':
      return (
        <RouteModule
          title={`${loaderData.internship.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['INTERNSHIP']}`}
          ogImage={
            loaderData.internship.isPostedAnonymously
              ? null
              : imageUrlForOgMeta({
                  data:
                    loaderData.internship.orgLogoOverride ??
                    loaderData.internship.org?.logo,
                  appConfig,
                })
          }
          description={loaderData.internship.descriptionTruncated.slice(0, 200)}
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.internship.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.internship.url[loaderData.internship.locale],
          }}
          schemaMarkups={[
            loaderData.internship.schemaInternshipMarkup,
            loaderData.internship.schemaBreadcrumbListMarkup?.[
              loaderData.locale
            ],
          ]}
        >
          <CreateListingApplicationFromTmpStorageContainer
            listing={loaderData.internship}
          >
            {({ initialListingApplicationId, listingApplicationFailure }) => (
              <ViewInternshipPageLoadable
                internship={loaderData.internship}
                listingApplicationId={
                  initialListingApplicationId ?? loaderData.applicationId
                }
                initiallySaved={loaderData.saved}
                orgInitiallySaved={loaderData.orgSaved}
                listingApplicationFailure={listingApplicationFailure}
              />
            )}
          </CreateListingApplicationFromTmpStorageContainer>
        </RouteModule>
      );
    case 'EXPIRED_INTERNSHIP':
      return (
        <RouteModule title={loaderData.internship.name}>
          <ExpiredInternshipPageLoadable
            internship={loaderData.internship}
            listingApplicationId={loaderData.applicationId}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_INTERNSHIP':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <UnpublishedInternshipPageLoadable
            unpublishedInternship={loaderData.unpublishedInternship}
          />
        </RouteModule>
      );
  }
}
